/* moose.lighting — My Projects
   ---------------------------------------------------------------------------
   Named lists of fixtures a customer builds while shopping, the way a designer
   or a contractor actually works: one list per room, per job, per client.

   STORAGE is deliberately a swappable layer.

     LocalStore    always on. Per browser. Works signed out, works today.
     AccountStore  switches itself on only when window.theme.lists_app exists,
                   which is what the "CustomerMeta, Wishlist Compare" app
                   defines once it is connected to the theme. Until then it
                   reports unavailable and nothing calls it. Same API shape the
                   theme's own StoreListsEngine uses for wishlist and compare,
                   namespace "projects" instead of "wishlist".

   SHARING never touches either one. A customer metafield cannot be read by a
   stranger, so a shared link would otherwise need a backend. The project
   encodes into the URL instead. The theme already does this for wishlists
   (/pages/wish-list?params=handle=variant+handle=variant); this is the same
   idea with quantities and a name.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var LS_KEY = 'moose-projects-v1';
  var NS = 'projects';
  var MAX_PROJECTS = 40;
  var MAX_ITEMS = 200;

  /* ---------------------------------------------------------------- utils */

  function uid() {
    return 'p' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  }

  function money(cents) {
    var f = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var amt = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return f.replace(/\{\{\s*amount[^}]*\}\}/, amt);
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function b64urlEncode(str) {
    return btoa(unescape(encodeURIComponent(str)))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function b64urlDecode(str) {
    str = str.replace(/-/g, '+').replace(/_/g, '/');
    while (str.length % 4) str += '=';
    return decodeURIComponent(escape(atob(str)));
  }

  /* ----------------------------------------------------------- local store */

  var LocalStore = {
    available: function () {
      try {
        localStorage.setItem('__mp', '1');
        localStorage.removeItem('__mp');
        return true;
      } catch (e) {
        return false;
      }
    },
    read: function () {
      try {
        var raw = localStorage.getItem(LS_KEY);
        var data = raw ? JSON.parse(raw) : null;
        if (!data || !Array.isArray(data.projects)) return { v: 1, projects: [] };
        return data;
      } catch (e) {
        return { v: 1, projects: [] };
      }
    },
    write: function (data) {
      try {
        localStorage.setItem(LS_KEY, JSON.stringify(data));
      } catch (e) {
        /* private mode, or quota. The page still works for this session. */
      }
    }
  };

  /* --------------------------------------------------------- account store
     Mirrors the theme's own StoreListsEngine calls. Key/value pairs land in
     the customer metafield namespace "projects":

        <projectId>__name  ->  url-encoded display name
        <projectId>__<variantId>  ->  "<handle>:<qty>"

     Keys stay inside Shopify's metafield key rules (alphanumeric, _ and -).  */

  var AccountStore = {
    available: function () {
      var t = window.theme;
      return !!(t && t.customer && t.lists_app && t.lists_app.url && t.lists_app.iid);
    },
    _params: function () {
      var t = window.theme;
      return {
        namespace: NS,
        customerid: t.customer_id,
        shop: t.permanent_domain,
        domain: t.host,
        iid: t.lists_app.iid
      };
    },
    _post: function (path, extra) {
      var body = new URLSearchParams();
      var p = this._params();
      Object.keys(p).forEach(function (k) { body.append(k, p[k]); });
      if (extra) {
        Object.keys(extra).forEach(function (k) {
          if (Array.isArray(extra[k])) {
            extra[k].forEach(function (v) { body.append(k + '[]', v); });
          } else {
            body.append(k, extra[k]);
          }
        });
      }
      return fetch('https://' + window.theme.lists_app.url + '/api/' + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      }).then(function (r) { return r.json(); });
    },
    read: function () {
      if (!this.available()) return Promise.resolve(null);
      return this._post('getlist').then(function (res) {
        if (!res || !res.items) return null;
        var byId = {};
        res.items.forEach(function (pair) {
          Object.keys(pair).forEach(function (key) {
            var cut = key.indexOf('__');
            if (cut < 0) return;
            var pid = key.slice(0, cut);
            var rest = key.slice(cut + 2);
            byId[pid] = byId[pid] || { id: pid, name: 'Untitled project', created: 0, items: [] };
            if (rest === 'name') {
              try { byId[pid].name = decodeURIComponent(pair[key]); }
              catch (e) { byId[pid].name = pair[key]; }
            } else {
              var bits = String(pair[key]).split(':');
              byId[pid].items.push({
                v: parseInt(rest, 10),
                h: bits[0],
                q: parseInt(bits[1], 10) || 1
              });
            }
          });
        });
        return { v: 1, projects: Object.keys(byId).map(function (k) { return byId[k]; }) };
      }).catch(function () { return null; });
    },
    write: function (data) {
      if (!this.available()) return Promise.resolve(false);
      var items = [];
      data.projects.forEach(function (p) {
        items.push(p.id + '__name=' + encodeURIComponent(p.name));
        p.items.forEach(function (it) {
          items.push(p.id + '__' + it.v + '=' + it.h + ':' + (it.q || 1));
        });
      });
      return this._post('massadd', { purge: 'yes', items: items })
        .then(function () { return true; })
        .catch(function () { return false; });
    }
  };

  /* ------------------------------------------------------------------ core */

  var Projects = {
    data: { v: 1, projects: [] },
    ready: false,
    _listeners: [],

    on: function (fn) { this._listeners.push(fn); },
    _emit: function () {
      var self = this;
      this._listeners.forEach(function (fn) {
        try { fn(self.data); } catch (e) { /* one bad listener must not stop the rest */ }
      });
    },

    init: function () {
      var self = this;
      self.data = LocalStore.read();
      self.ready = true;
      self._emit();

      /* If the account store is live, it is the source of truth. Anything the
         customer built while signed out is merged up, not thrown away. */
      if (AccountStore.available()) {
        AccountStore.read().then(function (remote) {
          if (!remote) return;
          self.data = self._merge(remote, self.data);
          LocalStore.write(self.data);
          AccountStore.write(self.data);
          self._emit();
        });
      }
    },

    _merge: function (primary, secondary) {
      var out = { v: 1, projects: primary.projects.slice() };
      var byName = {};
      out.projects.forEach(function (p) { byName[p.name.toLowerCase()] = p; });
      secondary.projects.forEach(function (p) {
        var hit = byName[p.name.toLowerCase()];
        if (!hit) { out.projects.push(p); return; }
        p.items.forEach(function (it) {
          var dup = hit.items.some(function (x) { return x.v === it.v; });
          if (!dup) hit.items.push(it);
        });
      });
      return out;
    },

    _persist: function () {
      LocalStore.write(this.data);
      if (AccountStore.available()) AccountStore.write(this.data);
      this._emit();
    },

    all: function () { return this.data.projects; },

    get: function (id) {
      return this.data.projects.filter(function (p) { return p.id === id; })[0] || null;
    },

    create: function (name) {
      name = (name || '').trim() || 'Untitled project';
      if (this.data.projects.length >= MAX_PROJECTS) return null;
      var p = { id: uid(), name: name, created: Date.now(), items: [] };
      this.data.projects.unshift(p);
      this._persist();
      return p;
    },

    rename: function (id, name) {
      var p = this.get(id);
      if (!p) return false;
      p.name = (name || '').trim() || p.name;
      this._persist();
      return true;
    },

    remove: function (id) {
      this.data.projects = this.data.projects.filter(function (p) { return p.id !== id; });
      this._persist();
    },

    addItem: function (id, variantId, handle, qty) {
      var p = this.get(id);
      if (!p) return false;
      if (p.items.length >= MAX_ITEMS) return false;
      variantId = parseInt(variantId, 10);
      var hit = p.items.filter(function (x) { return x.v === variantId; })[0];
      if (hit) hit.q = (hit.q || 1) + (qty || 1);
      else p.items.push({ v: variantId, h: handle, q: qty || 1 });
      this._persist();
      return true;
    },

    removeItem: function (id, variantId) {
      var p = this.get(id);
      if (!p) return;
      variantId = parseInt(variantId, 10);
      p.items = p.items.filter(function (x) { return x.v !== variantId; });
      this._persist();
    },

    setQty: function (id, variantId, qty) {
      var p = this.get(id);
      if (!p) return;
      variantId = parseInt(variantId, 10);
      qty = Math.max(1, parseInt(qty, 10) || 1);
      p.items.forEach(function (x) { if (x.v === variantId) x.q = qty; });
      this._persist();
    },

    countIn: function (variantId) {
      variantId = parseInt(variantId, 10);
      return this.data.projects.filter(function (p) {
        return p.items.some(function (x) { return x.v === variantId; });
      }).length;
    },

    totalItems: function () {
      return this.data.projects.reduce(function (n, p) { return n + p.items.length; }, 0);
    },

    /* ---- sharing: the whole project travels in the URL, no backend ---- */

    encode: function (p) {
      var body = p.items.map(function (i) { return i.h + ':' + i.v + ':' + (i.q || 1); }).join('|');
      return b64urlEncode(p.name + '~' + body);
    },

    decode: function (token) {
      try {
        var raw = b64urlDecode(token);
        var cut = raw.indexOf('~');
        if (cut < 0) return null;
        var name = raw.slice(0, cut);
        var items = raw.slice(cut + 1).split('|').filter(Boolean).map(function (chunk) {
          var b = chunk.split(':');
          return { h: b[0], v: parseInt(b[1], 10), q: parseInt(b[2], 10) || 1 };
        }).filter(function (i) { return i.h && i.v; });
        if (!items.length) return null;
        return { id: null, name: name || 'Shared project', created: 0, items: items };
      } catch (e) {
        return null;
      }
    },

    shareUrl: function (p) {
      return location.origin + '/pages/my-projects?shared=' + this.encode(p);
    },

    /* ---- cart ---- */

    addAllToCart: function (p) {
      if (!p.items.length) return Promise.resolve({ ok: false, reason: 'empty' });
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: p.items.map(function (i) { return { id: i.v, quantity: i.q || 1 }; })
        })
      }).then(function (r) {
        return r.json().then(function (body) {
          return { ok: r.ok, status: r.status, body: body };
        });
      });
    }
  };

  /* ------------------------------------------------- product data, cached */

  var productCache = {};

  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch('/products/' + handle + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return productCache[handle];
  }

  window.MooseProjects = Projects;
  window.MooseProjectsInternal = {
    LocalStore: LocalStore,
    AccountStore: AccountStore,
    fetchProduct: fetchProduct,
    money: money,
    esc: esc
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { Projects.init(); });
  } else {
    Projects.init();
  }
})();
